import { useId, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useEvent } from "shared/lib/useEvent"
import { useFormSubmit } from "shared/lib/useFormSubmit"
import { Action } from "shared/types/Action"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { ProjectFormData } from "features/project-management/types/ProjectFormData"

import { ProjectPageProjectSidebarProps } from "../ProjectPageProjectSidebar"
import { ProjectPageProjectSidebarViewModel } from "../ProjectPageProjectSidebarView"

export function useProjectPageProjectSidebarViewModel({
  onSubmit,
  hideControls,
  model,
  isEditing,
  enterEditing,
  exitEditing,
  sidebarActiveTab,
  setSidebarActiveTab,
  ...rest
}: ProjectPageProjectSidebarProps): ProjectPageProjectSidebarViewModel {
  const { t } = useTranslation()
  const formID = `project-form-${useId()}`

  const isReadonly = !isEditing

  const submit = useEvent((formData: ProjectFormData) => onSubmit?.(formData))

  const { handleSubmit, isSubmitting } = useFormSubmit<ProjectFormData>({
    onSubmit: submit,
    onSuccess: exitEditing
  })

  const editingActions = useMemo<Action[]>(
    () => [
      {
        id: "project-sidebar__edit__cancel",
        "data-testid": "project-sidebar__edit__cancel",
        onClick: exitEditing,
        icon: IconName.CLOSE_CIRCLE,
        label: t("General.Cancel")
      },
      {
        id: "project-sidebar__edit__submit",
        "data-testid": "project-sidebar__edit__submit",
        form: formID,
        type: "submit",
        icon: IconName.CHECK_CIRCLE,
        label: t("General.Confirm"),
        disabled: isSubmitting
      }
    ],
    [exitEditing, formID, isSubmitting, t]
  )

  const normalActions = useMemo<Action[]>(
    () => [
      {
        id: "project-sidebar__edit",
        "data-testid": "project-sidebar__edit",
        onClick: enterEditing,
        icon: IconName.EDIT,
        label: t("Project.Actions.Update")
      }
    ],
    [enterEditing, t]
  )

  return {
    ...rest,
    formID,
    model,
    isReadonly,
    isDisabled: isSubmitting,
    hideControls,
    onSubmit: handleSubmit,
    actions: isReadonly ? normalActions : editingActions,
    sidebarActiveTab,
    setSidebarActiveTab
  }
}
